import React, { FC, ReactNode, useContext } from "react";
import { useSelector, useDispatch } from "react-redux";
import { isDirty, reset } from "@mds/common/components/forms/form";
import { FormContext } from "./FormWrapper";
import { closeModal } from "@mds/common/redux/actions/modalActions";
import { Modal, ModalFuncProps } from "antd";
import { BaseButtonProps } from "antd/lib/button/button";
import CoreButton from "../common/CoreButton";

export const cancelConfirmWrapper = (
  cancelFunction,
  isFormDirty: boolean,
  modalProps?: ModalFuncProps
) => {
  return !isFormDirty
    ? cancelFunction()
    : Modal.confirm(
      modalProps ?? {
        title: "Discard changes?",
        content: "All changes made will not be saved.",
        onOk: cancelFunction,
        cancelText: "Continue Editing",
        okText: "Discard",
      }
    );
};

interface RenderCancelButtonProps {
  buttonText?: string | ReactNode;
  viewButtonText?: string | ReactNode;
  buttonProps?: BaseButtonProps;
  cancelFunction?: () => void | Promise<void>;
  cancelModalProps?: ModalFuncProps;
  iconButton?: boolean;
  disabled?: boolean;
  loading?: boolean;
  resetForm?: boolean;
}

/**
 * a generic cancel button
 * will close a modal if it's in one
 * - no need to pass in a cancelFunction if that's all that's desired
 * - will show a confirm if the form is dirty
 * Uses FormContext to determine if form is dirty or within a modal
 * - intended for use with FormWrapper
 */
const RenderCancelButton: FC<RenderCancelButtonProps> = ({
  buttonText = "Cancel",
  viewButtonText = "Close",
  buttonProps = { type: "default" },
  cancelFunction,
  cancelModalProps,
  iconButton = false,
  disabled = false,
  loading = false,
  resetForm = false,
}) => {
  const dispatch = useDispatch();
  const { formName, isModal, isEditMode } = useContext(FormContext);
  const isFormDirty = useSelector(isDirty(formName));

  const handleCancel = () => {
    if (cancelFunction) {
      cancelFunction();
    }
    if (resetForm) {
      dispatch(reset(formName));
    }
    if (isModal) {
      dispatch(closeModal());
    }
  };

  const buttonCancelFunction = isEditMode
    ? () => cancelConfirmWrapper(handleCancel, isFormDirty, cancelModalProps)
    : handleCancel;

  const buttonType = buttonProps?.type ?? "default";
  const buttonLabel = isEditMode ? buttonText : viewButtonText;
  const className = `${buttonProps?.className ?? ""} form-btn`;

  return (
    <CoreButton aria-label="Cancel" loading={loading} disabled={disabled} {...buttonProps} className={className} type={buttonType} onClick={() => buttonCancelFunction()}>
      {!iconButton && buttonLabel}
    </CoreButton>
  );
};

export default RenderCancelButton;
